import classnames from 'classnames';
import useClickOutside from 'hooks/useClickOutside';
import React, { useRef } from 'react';
import ModalBody from '~/reusable-components/dialogs/Modal/ModalBody';
import ModalHeader from '~/reusable-components/dialogs/Modal/ModalHeader';
import Overlay from '~/reusable-components/dialogs/Overlay';

const CSS = require('./Modal.module.scss');

interface ModalProps {
  title?: any;
  onClose?: () => void;
  onReset?: () => void;
  fullscreen?: 'all' | 'mobile';
  isWide?: boolean;
  variant?: 'white' | 'sandstone' | 'success';
  className?: string;
  bodyClassName?: string;
  containerSize?: 'small' | 'inbetween' | 'medium';
  footer?: React.ReactNode;
  children: React.ReactNode;
  closeOnOutsideClick?: boolean;
  hideHeader?: boolean;
}

function Modal({
  children,
  title,
  onClose,
  onReset,
  fullscreen,
  isWide,
  variant = 'sandstone',
  bodyClassName,
  containerSize,
  footer,
  className,
  closeOnOutsideClick = true,
  hideHeader = false,
}: ModalProps) {
  const modalRef = useRef(null);
  useClickOutside(modalRef, () => {
    if (closeOnOutsideClick && onClose) {
      onClose();
    }
  });

  const classes = classnames(CSS.Modal, className, {
    [CSS.ModalFullscreen]: fullscreen === 'all',
    [CSS.ModalFullscreenMobile]: fullscreen === 'mobile',
    [CSS.ModalNarrow]: !isWide,
  });
  const resolvedContainerSize =
    fullscreen === 'all'
      ? undefined
      : (containerSize ?? (isWide ? 'medium' : 'small'));
  return (
    <Overlay
      onEsc={onClose}
      portalId="modal"
      containerSize={resolvedContainerSize}
      fullscreen={fullscreen}
    >
      <dialog ref={modalRef} className={classes} data-variant={variant}>
        {!hideHeader && (
          <ModalHeader
            title={title}
            onClose={onClose}
            onReset={onReset}
            variant={variant}
          />
        )}
        <ModalBody data-variant={variant} className={bodyClassName}>
          {children}
        </ModalBody>
        {footer}
      </dialog>
    </Overlay>
  );
}

export default Modal;
